import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Stack, TextField } from '@mui/material';
// components
import TextMaxLine from '../../../components/TextMaxLine';
import { FormProvider, RHFSelect, RHFSwitch, RHFTextField } from '../../../components/hook-form';
import axiosInstance from 'src/utils/axios';
import { PromoCodeType, PROMO_SOURCES } from 'src/@types/PromoCode';
import { APIS } from 'src/utils/serviceUrls';
import { styled } from '@mui/material/styles';

// ----------------------------------------------------------------------

// Module scope: defined inside the component it was a new component type on
// every render, so the date input re-mounted and kept only the first key.
const StyledTextField = styled(TextField)({
  '& .MuiFormHelperText-root': {
    color: 'red',
  },
});

type Props = {
  isEdit: boolean;
  currentPromoCode?: PromoCodeType;
  handleClose(): void;
};

export default function PromoCodeNewEditForm({ isEdit, currentPromoCode, handleClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [expiryDateError, setExpiryDateError] = useState<string>('');
  // The API's refusal, shown inside the modal (a snackbar can sit behind it).
  const [submitError, setSubmitError] = useState<string>('');
  // Diplomas (with their batches) for the optional scope selects.
  const [diplomas, setDiplomas] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      const res = await axiosInstance.get(APIS.DIPLOMAS.LIST);
      const d: any = res.data?.data ?? res.data;
      setDiplomas(Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : []);
    })().catch(() => setDiplomas([]));
  }, []);

  const NewPromoCodeSchema = Yup.object().shape({
    amount: Yup.number()
      .min(1, 'percentage minimum is 1%')
      .max(100, 'percentage maximum is 100%')
      .required('percentage  is required'),
    code: Yup.string().required('code is required'),
    expiryDate: Yup.string().min(3).required('Expiry Date is required'),
    usageLimit: Yup.string().required('Usage Limit is Required'),
  });

  const defaultValues: PromoCodeType = useMemo(
    () => ({
      amount: currentPromoCode?.amount || 0,
      code: currentPromoCode?.code || '',
      expiryDate: currentPromoCode?.expiryDate || new Date(),
      isActive: currentPromoCode?.isActive || true,
      usageLimit: currentPromoCode?.usageLimit || 1,
      // The form has no Type field; the API only accepts PERCENTAGE | FIXED.
      type: currentPromoCode?.type || 'PERCENTAGE',
      source: currentPromoCode?.source || 'admin',
      partnerName: currentPromoCode?.partnerName || '',
      diploma: (currentPromoCode?.diploma as any) ? String(currentPromoCode?.diploma) : '',
      batchId: (currentPromoCode?.batchId as any) ? String(currentPromoCode?.batchId) : '',
    }),
    [currentPromoCode]
  );

  const methods = useForm<PromoCodeType>({
    resolver: yupResolver(NewPromoCodeSchema),
    defaultValues,
  });
  const date = new Date();
  const today = dayjs();

  const {
    reset,
    setValue,
    watch,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isSubmitting },
  } = methods;

  const handleExpiryDateChange = (newValue: any) => {
    setValue('expiryDate', newValue ? newValue : '');
    if (dayjs(newValue) < today) {
      setExpiryDateError('Expiry date should be after today');
      setError('expiryDate', { type: 'custom', message: 'Expiry date should be after today' });
    } else {
      setExpiryDateError('');
      clearErrors('expiryDate');
    }
  };

  const values = watch();

  // Keep the batch choice consistent with the selected program: if the current
  // batchId isn't one of the chosen diploma's batches (program changed/cleared),
  // reset it. Skips while diplomas are still loading so an edit-load isn't wiped.
  useEffect(() => {
    if (!values.batchId) return;
    if (!values.diploma) {
      setValue('batchId', '');
      return;
    }
    if (!diplomas.length) return;
    const batches = diplomas.find((d: any) => String(d._id) === String(values.diploma))?.batches ?? [];
    const stillValid = batches.some((b: any) => String(b._id) === String(values.batchId));
    if (!stillValid) setValue('batchId', '');
  }, [values.diploma, values.batchId, diplomas, setValue]);

  useEffect(() => {
    if (isEdit && currentPromoCode) {
      reset(currentPromoCode);
      if (dayjs(currentPromoCode.expiryDate) < today) {
        setExpiryDateError('Expiry date should be after today');
        console.log('testing');
      }
    }
    if (!isEdit) {
      reset(currentPromoCode);
    }
  }, [isEdit, currentPromoCode]);

  const onSubmit = async (data: PromoCodeType) => {
    setSubmitError('');
    try {
      // Partner name is required when the source is a partner.
      if (data.source === 'partner' && !data.partnerName?.trim()) {
        setError('partnerName', { type: 'custom', message: 'Partner name is required' });
        enqueueSnackbar('Partner name is required', { variant: 'error' });
        return;
      }
      let submitValues = {
        ...data,
        type: data.type || 'PERCENTAGE',
        // Only keep a partner name when the source is a partner.
        partnerName: data.source === 'partner' ? data.partnerName?.trim() : '',
        usageLimit: +data.usageLimit,
        expiryDate: new Date(data.expiryDate),
        // Scope: send null (not '') when unset so the BE's optional @IsMongoId
        // skips it. A batch can't be set without its diploma.
        diploma: data.diploma ? data.diploma : null,
        batchId: data.diploma && data.batchId ? data.batchId : null,
      };
      if (currentPromoCode)
        await axiosInstance.patch(
          APIS.PROMO_CODE.PATCH(currentPromoCode._id as string),
          submitValues
        );
      else await axiosInstance.post(APIS.PROMO_CODE.ADD, submitValues);
      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose();
    } catch (err) {
      const apiMessage =
        (Array.isArray(err?.messages) && err.messages.length && err.messages[0]) ||
        (typeof err?.message === 'string' && err.message) ||
        (typeof err === 'string' && err) ||
        '';
      const message =
        apiMessage || (!isEdit ? 'Unable to create PromoCode!' : 'Unable to edit PromoCode');
      setSubmitError(message);
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Stack height={'100%'}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <TextMaxLine marginBottom={'20px'} marginTop={'20px'} fontSize={'22px'}>
          {isEdit ? ' Edit Promo Code' : ' Create Promo Code'}
        </TextMaxLine>
        <Stack direction={'column'} justifyContent={'space-between'} height={'90%'}>
          <Stack direction={'column'} spacing={5}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 5,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField type={'text'} name="code" label="Code" />
              <RHFTextField type={'number'} name="amount" label="percentage" />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Expiry Date"
                  inputFormat="MM/DD/YYYY"
                  value={values.expiryDate}
                  onChange={handleExpiryDateChange}
                  renderInput={(params) => (
                    <StyledTextField
                      error={expiryDateError ? true : false}
                      helperText={expiryDateError}
                      {...params}
                    />
                  )}
                />
              </LocalizationProvider>

              <RHFTextField type={'number'} name="usageLimit" label="Usage Limit" />

              <RHFSelect name="source" label="Source">
                {PROMO_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </RHFSelect>

              {/* Partner name appears (and is required) when source = partner. */}
              {values.source === 'partner' && (
                <RHFTextField name="partnerName" label="Partner name" />
              )}

              <RHFSwitch name="isActive" label="Is PromoCode Active" />

              {/* Optional scope. Leave "All programs" ⇒ the code is GLOBAL. Pick
                  a program to limit it to that diploma; then optionally pick one
                  batch to limit it to that single cohort. */}
              <RHFSelect name="diploma" label="Program (optional)">
                <option value="">All programs (global)</option>
                {diplomas.map((d: any) => (
                  <option key={d._id} value={d._id}>
                    {d.title || d.name}
                  </option>
                ))}
              </RHFSelect>

              {values.diploma ? (
                <RHFSelect name="batchId" label="Batch (optional)">
                  <option value="">All batches of this program</option>
                  {(
                    diplomas.find((d: any) => String(d._id) === String(values.diploma))?.batches ??
                    []
                  ).map((b: any) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </RHFSelect>
              ) : null}
            </Box>
          </Stack>

          <Stack alignItems="flex-end" spacing={2} sx={{ mt: 3 }}>
            {submitError && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {submitError}
              </Alert>
            )}
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type={expiryDateError ? undefined : 'submit'}
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create PromoCode' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
